import DOMPurify, { type Config } from "dompurify";
import { KeyboardEvent, MouseEvent } from "react";
import { onLinkClickHandler } from "../utils";
import { createStylesheet } from "../utils/styles";

export interface Props {
  html?: string;
  variant?: "inline" | "article";
}

// "inline" is for user-supplied content (ticket descriptions, comments): only
// the inline formatting the backend legitimately emits, nothing that can run
// script or load remote resources. "article" is for CMS-authored help
// articles, which need richer markup (headings, images, tables).
const sanitizeConfig: Record<NonNullable<Props["variant"]>, Config> = {
  inline: {
    ALLOWED_TAGS: ["a", "b", "strong", "i", "em", "u", "p", "br", "span", "ul", "ol", "li"],
    ALLOWED_ATTR: ["href", "class"],
  },
  article: {
    USE_PROFILES: { html: true },
  },
};

export const UIHtml = ({ html = "", variant = "inline" }: Props) => {
  const classes = useStyles();
  const parsedHtml = DOMPurify.sanitize(html.replace(/\n/g, "<br />"), sanitizeConfig[variant]) as string;
  return (
    <div
      className={classes.ctr}
      role="button"
      tabIndex={0}
      onClick={(ev: MouseEvent<HTMLElement>) => onLinkClickHandler(ev, history)}
      onKeyDown={(ev: KeyboardEvent<HTMLElement>) => {
        if (ev.key === "Enter" || ev.key === " ") {
          onLinkClickHandler(ev as any, history);
        }
      }}
      dangerouslySetInnerHTML={{
        __html: parsedHtml,
      }}
    />
  );
};

const useStyles = createStylesheet((theme) => ({
  ctr: {
    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "none",
    },
    "& a:hover": {
      color: theme.palette.primary.main,
      textDecoration: "underline",
    },
  },
}));

export default UIHtml;
